// proposed-change-wire-mapping.ts
//
// Maps ONE wire-contract proposed change (§5.3 `AutopilotResultSchema` shape:
// op/target/expected_revision/replacement_ids/content) to
// proposal-applier.ts's transaction-input shape (op/ticket_id/patch/
// new_ticket) — the two shapes exist for different audiences (wire contract
// vs. in-memory transaction input) and were never meant to be the same type.
// Split out of supervisor-proposal-application.ts (phase 07, D12) to keep
// that file under the 200-line budget; behaviour unchanged.
import type { AutopilotResult } from "./autopilot-result-schema.js";
import { isBootstrapTicketKind } from "./feature-dispatch-gate-guard.js";
import type { ProposedChange as ApplierChange } from "./proposal-applier.js";
import { summarizeSchemaIssues } from "./schema-issue-summary.js";
import { ProposedChangeContentSchema, type Ticket } from "./schemas/ticket-schema.js";
import { nextTicketId } from "./ticket-file-persistence.js";

export type ToApplierChangeResult =
  | { ok: true; change: ApplierChange }
  | { ok: false; reason: string };

/**
 * `content` for a "create" op is expected to be a Ticket-shaped object
 * MINUS `id`; the caller assigns a fresh id (worker never gets to pick its
 * own ticket id — that would let a proposal collide with/overwrite an
 * unrelated ticket).
 *
 * Uses `safeParse` (never throws): a real worker's `content` field is
 * untrusted wire input, same as any other proposal field — a malformed
 * shape (dogfooding found a real Claude worker emit a plain string here)
 * must reject gracefully like every other bad-proposal case (stale
 * expected_revision, unknown target), not crash the whole supervisor loop
 * with an unhandled exception (found via real E2E run, mission
 * ME-2026-0097d9 — `avatar autopilot run` exited on an uncaught ZodError
 * with zero ticket/journal context, see phase-10 dogfood evidence).
 */
export function toApplierChange(
  change: AutopilotResult["proposed_changes"][number],
  tickets: Ticket[],
): ToApplierChangeResult {
  // Audit 2026-09-14 finding 1: this guard used to live ONLY inside the
  // `create` branch below, so `amend` reached the fall-through at the bottom of
  // this function and cast `change.content` straight to `Partial<Ticket>` —
  // promoting an ordinary ticket into `survey`/`plan`, the two kinds
  // feature-dispatch-gate-guard.ts exempts from G0/G1. A real mission
  // (ME-2026-7a1a75) amended T-0101/T-0102 into `survey` and both ran and
  // committed while both gates still said `pending`.
  //
  // Hoisted here so it covers EVERY op, present and future: the failure mode
  // was not "amend was forgotten", it was "the guard was attached to one
  // branch of a function that has several".
  const proposedKind = (change.content as { kind?: unknown } | undefined)?.kind;
  if (typeof proposedKind === "string" && isBootstrapTicketKind(proposedKind as Ticket["kind"])) {
    return {
      ok: false,
      reason:
        `proposal không được ${change.op} ticket sang kind "${proposedKind}" (bootstrap kind) — ` +
        "chỉ initializer của controller được tạo survey/plan; kind bootstrap né được gate G0/G1.",
    };
  }

  if (change.op === "create") {
    // The SAME export the pack publishes as proposed-change-content.schema.json
    // — a worker that reads the schema gets the shape enforced here (dogfood
    // ME-2026-42f25f: a private `omit({ id })` here vs a published ticket
    // schema requiring `id` rejected a worker that did exactly as told).
    const parsed = ProposedChangeContentSchema.safeParse(change.content);
    if (!parsed.success) {
      return {
        ok: false,
        reason:
          "content không khớp proposed-change-content.schema.json (ticket KHÔNG có `id` — id lấy từ target): " +
          summarizeSchemaIssues(parsed.error),
      };
    }
    // Bug K (P15): `kind` here comes from WORKER output, and the Bug I gate
    // guard exempts survey/plan from the G0/G1 check (they produce the very
    // artifacts those gates approve). T-0002 `plan` is itself exempt, so it
    // runs pre-approval and proposes the mission's tickets — left unchecked
    // it could propose every ticket as `survey` and the whole mission would
    // execute before anyone approved anything, making the Bug I fix
    // worthless. Only the controller (mission-initializer-tickets.ts) may
    // create a bootstrap-kind ticket.
    if (isBootstrapTicketKind(parsed.data.kind)) {
      return {
        ok: false,
        reason:
          `proposal không được tạo ticket kind "${parsed.data.kind}" (bootstrap kind) — ` +
          "chỉ initializer của controller được tạo survey/plan; kind bootstrap né được gate G0/G1.",
      };
    }
    return {
      ok: true,
      change: { op: "create", new_ticket: { ...parsed.data, id: nextTicketId(tickets) } },
    };
  }
  // CXR-05: an amend patch is a partial ticket — a string/array/null spread
  // into a ticket is nonsense (proposal-applier.ts also schema-checks the result).
  const content = change.content as unknown;
  if (
    change.op === "amend" &&
    content !== undefined &&
    (typeof content !== "object" || content === null || Array.isArray(content))
  ) {
    return { ok: false, reason: "amend content phải là object (một phần ticket.schema)." };
  }
  return {
    ok: true,
    change: {
      op: change.op,
      ticket_id: change.target,
      expected_revision: change.expected_revision,
      patch: change.op === "amend" ? (change.content as Partial<Ticket> | undefined) : undefined,
    },
  };
}
